import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toPascal } from './utils';

function goType(t: string): string {
  return t === 'number' ? 'float64' : t === 'boolean' ? 'bool' : t === 'array' ? '[]interface{}' : t === 'object' ? 'map[string]interface{}' : 'string';
}

function genConfig(config: ExportConfig): string {
  const appConsts = config.apps
    .map((a) => `\t${toPascal(a.key)}App = "${a.key}"`)
    .join("\n");

  const templateConsts = config.apps
    .flatMap((a) => a.templates.map((t) => `\t${toPascal(t.camelName)}Template = "${t.camelName}"`))
    .join("\n");

  const appMap = config.apps
    .map((a) => `\t\t${toPascal(a.key)}App: {FromEmail: "${a.fromEmail}", KeyEnv: "${a.envVarName}"}`)
    .join(",\n");

  const templateMap = config.apps
    .flatMap((a) =>
      a.templates.map((t) => `\t\t${toPascal(t.camelName)}Template: {App: ${toPascal(a.key)}App, Slug: "${t.slug}"}`)
    )
    .join(",\n");

  return `// mail_config.go — generated by Mail Service SDK Export
package mail

// App keys
const (
${appConsts}
)

// Template keys
const (
${templateConsts}
)

// AppConfig holds the from-email and environment variable name for an app.
type AppConfig struct {
\tFromEmail string
\tKeyEnv    string
}

// TemplateConfig links a template to its app and slug.
type TemplateConfig struct {
\tApp  string
\tSlug string
}

var MailApps = map[string]AppConfig{
${appMap},
}

var MailTemplates = map[string]TemplateConfig{
${templateMap},
}
`;
}

function genService(config: ExportConfig): string {
  const typedMethods = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const fnName = `Send${toPascal(t.camelName)}`;
        if (t.schema && t.schema.length > 0) {
          const structFields = t.schema
            .map((f) => `\t${toPascal(f.key)} ${goType(f.type)} \`json:"${f.key}"\``)
            .join("\n");
          const structName = `${toPascal(t.camelName)}Data`;
          return `// ${structName} holds the payload for the ${t.name} template.\ntype ${structName} struct {\n${structFields}\n}\n\nfunc (m *MailService) ${fnName}(recipient string, data ${structName}) (*MailResponse, error) {\n\treturn m.Send("${t.camelName}", recipient, data)\n}`;
        }
        return `func (m *MailService) ${fnName}(recipient string, data map[string]interface{}) (*MailResponse, error) {\n\treturn m.Send("${t.camelName}", recipient, data)\n}`;
      }),
    )
    .join("\n\n");

  return `// mail_service.go — generated by Mail Service SDK Export
package mail

import (
\t"bytes"
\t"encoding/json"
\t"fmt"
\t"net/http"
\t"os"
\t"strings"
)

type MailResponse struct {
\tSuccess   bool   \`json:"success"\`
\tMessageID string \`json:"messageId,omitempty"\`
\tError     string \`json:"error,omitempty"\`
}

type MailService struct {
\tServiceURL string
}

func NewMailService() *MailService {
\turl := strings.TrimRight(os.Getenv("${config.serviceUrlVarName}"), "/")
\treturn &MailService{ServiceURL: url}
}

func (m *MailService) getKey(app string) string {
\treturn os.Getenv(MailApps[app].KeyEnv)
}

func (m *MailService) post(app, path string, body interface{}) (*MailResponse, error) {
\tif m.ServiceURL == "" || m.getKey(app) == "" {
\t\treturn &MailResponse{Success: false, Error: "not configured"}, nil
\t}
\tpayload, _ := json.Marshal(body)
\treq, err := http.NewRequest(http.MethodPost, fmt.Sprintf("%s%s", m.ServiceURL, path), bytes.NewReader(payload))
\tif err != nil {
\t\treturn nil, err
\t}
\treq.Header.Set("Content-Type", "application/json")
\treq.Header.Set("X-API-KEY", m.getKey(app))
\tresp, err := http.DefaultClient.Do(req)
\tif err != nil {
\t\treturn nil, err
\t}
\tdefer resp.Body.Close()
\tvar result MailResponse
\tjson.NewDecoder(resp.Body).Decode(&result)
\treturn &result, nil
}

func (m *MailService) Send(template, recipient string, data interface{}) (*MailResponse, error) {
\tt := MailTemplates[template]
\treturn m.post(t.App, "/v1/send", map[string]interface{}{
\t\t"template_slug": t.Slug,
\t\t"recipient":     recipient,
\t\t"data":          data,
\t})
}

func (m *MailService) SendRaw(app, subject, html, recipient, fromName string) (*MailResponse, error) {
\treturn m.post(app, "/v1/send/raw", map[string]interface{}{
\t\t"subject": subject, "html": html, "recipient": recipient, "from_name": fromName,
\t})
}

${typedMethods}
`;
}

export const goGenerator: LanguageGenerator = {
  id: 'go',
  name: 'Go',
  category: 'full-sdk',
  color: '#00ADD8',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'mail_config.go', content: genConfig(config), syntax: 'go' },
      { filename: 'mail_service.go', content: genService(config), syntax: 'go' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
